import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef } from 'react';
import { TimecodeDisplay } from './TimecodeDisplay';
import { formatTimecode, timecodeToSeconds } from '../utils/timecodeFormatter';
import { useTooltip } from "../../hooks/useTooltip";
export function TimecodePopover({ value, duration, fps, startFrame = 0, dropFrame = false, isOpen, onToggle, onSeek, }) {
    const containerRef = useRef(null);
    const popoverRef = useRef(null);
    // Set while we programmatically blur the timecode input to commit its value.
    // The blur fires focusout (relatedTarget null), which would otherwise be
    // treated as "focus left the popover" and trigger an extra close toggle.
    const isSeekingRef = useRef(false);
    const currentTC = formatTimecode(value, fps, startFrame, dropFrame);
    const isCopyingRef = useRef(false);
    // Create timecode-input imperatively so React doesn't re-render it
    // on every currentTime change (which would overwrite user edits)
    useEffect(() => {
        if (!isOpen || !popoverRef.current)
            return;
        const tcInput = document.createElement('timecode-input');
        tcInput.setAttribute('value', currentTC);
        tcInput.setAttribute('timebase', String(Math.round(fps)));
        if (dropFrame)
            tcInput.setAttribute('drop-frame', '1');
        tcInput.setAttribute("allow-relative", "1");
        // Insert before the Go button so the input appears first
        const placeholder = popoverRef.current.querySelector('.temp-placeholder');
        if (placeholder) {
            placeholder.replaceWith(tcInput);
        }
        // Focus the hours field inside the shadow DOM
        const focusTimer = setTimeout(() => {
            const hours = tcInput.shadowRoot?.querySelector('#hours');
            hours?.focus();
            hours?.select();
        }, 50);
        return () => {
            clearTimeout(focusTimer);
            tcInput.remove();
        };
        // Only run when the popover opens/closes — NOT when currentTC changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
    // Close on outside click
    useEffect(() => {
        if (!isOpen)
            return;
        const handleOutsideClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                onToggle();
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen, onToggle]);
    // Close on focus leaving the popover (WCAG 2.1.2 — Tab takes focus out
    // of the popover, so the popover should close rather than linger).
    // focusout fires before the new element receives focus, so e.relatedTarget
    // tells us where focus is going. If it's outside the container, close.
    useEffect(() => {
        if (!isOpen)
            return;
        const handleFocusOut = (e) => {
            // Ignore the focusout caused by our own programmatic blur during seek;
            // seekToPopoverValue handles the close itself.
            if (isSeekingRef.current) {
                isSeekingRef.current = false;
                return;
            }
            // Ignore if the user clicked Copy
            if (isCopyingRef.current) {
                isCopyingRef.current = false;
                return;
            }
            const next = e.relatedTarget;
            if (containerRef.current && (!next || !containerRef.current.contains(next))) {
                onToggle();
            }
        };
        const el = containerRef.current;
        el?.addEventListener('focusout', handleFocusOut);
        return () => el?.removeEventListener('focusout', handleFocusOut);
    }, [isOpen, onToggle]);
    // Seek to the timecode currently entered in the popover input
    const seekToPopoverValue = useCallback(() => {
        const tcInput = popoverRef.current?.querySelector('timecode-input');
        isSeekingRef.current = true;
        tcInput?.blur();
        let tc = tcInput?.getAttribute('value');
        if (tc && onSeek) {
            // Turn . into 00, but only if they're at the end
            if (tc.endsWith("...."))
                tc = "00:00:00:00";
            if (tc.endsWith("..."))
                tc = tc.substring(0, tc.length - 3) + ":00:00:00";
            if (tc.endsWith(".."))
                tc = tc.substring(0, tc.length - 2) + ":00:00";
            if (tc.endsWith("."))
                tc = tc.substring(0, tc.length - 1) + ":00";
            // Strip remaining dots
            tc = tc.replace(/\./g, ":");
            // Check if it's relative
            let components = tc.split(/[\:\;]/g);
            // Filter blank entries but only at the start
            const firstNonEmpty = components.findIndex((el) => el.trim() !== "");
            if (firstNonEmpty > 0) {
                components = components.slice(firstNonEmpty);
            }
            const isRelative = components.length < 4;
            while (components.length < 4) {
                components.splice(0, 0, "00");
            }
            tc = components.join(":");
            let seconds = timecodeToSeconds(tc, fps, (isRelative ? 0 : startFrame), dropFrame);
            if (isRelative) {
                seconds += timecodeToSeconds(currentTC, fps, startFrame, dropFrame);
            }
            if (seconds >= 0 && seconds <= duration) {
                onSeek(seconds);
            }
        }
        onToggle();
    }, [fps, startFrame, dropFrame, duration, onSeek, onToggle, currentTC]);
    // Handle keys in the popover: Enter to seek, Escape to close,
    // and stop ALL key events from reaching the player's keyboard
    // handler (which would otherwise trigger marker navigation,
    // frame stepping, etc. while the user is editing the timecode).
    const handlePopoverKeyDown = useCallback((e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
            e.preventDefault();
            seekToPopoverValue();
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            onToggle();
        }
    }, [seekToPopoverValue, onToggle]);
    // Copy button
    const copyBtnRef = useRef(null);
    const copyStatusTooltip = useTooltip(copyBtnRef, {
        trigger: 'manual',
        // Tipsy gravity names the tooltip's arrow edge, not its placement: 's'
        // puts the arrow on the tooltip's bottom, so the tooltip sits ABOVE the
        // button ('n' would place it below).
        gravity: 's',
        offset: 2,
    });
    const showCopyTooltip = useCallback((message) => {
        if (!copyBtnRef.current)
            return;
        copyStatusTooltip.show(message);
        setTimeout(() => {
            copyStatusTooltip.hide();
        }, 1500);
    }, [copyBtnRef, copyStatusTooltip]);
    const handleCopy = () => {
        // The input will lose focus so make sure it doesn't hide the popover
        isCopyingRef.current = true;
        const tcInput = popoverRef.current?.querySelector('timecode-input');
        tcInput?.copy();
        showCopyTooltip('Copied!');
    };
    return (_jsxs("div", { ref: containerRef, className: "hekate-player-timecode-container", children: [_jsx(TimecodeDisplay, { value: value, fps: fps, startFrame: startFrame, dropFrame: dropFrame, onClick: onToggle }), isOpen && (_jsxs("div", { ref: popoverRef, className: "hekate-player-timecode-popover", onKeyDown: handlePopoverKeyDown, children: [_jsx("h4", { style: { textAlign: 'center' }, children: "Seek to Timecode" }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '1px' }, children: [_jsx("div", { className: "temp-placeholder" }), _jsx("button", { ref: copyBtnRef, tabIndex: 0, className: "img-button tooltip", "aria-label": "Copy", onMouseDown: () => isCopyingRef.current = true, onClick: (e) => {
                                    e.preventDefault();
                                    handleCopy();
                                }, children: "\u29C9" })] }), _jsx("button", { type: "button", className: "hekate-player-timecode-popover-btn actionbtn", onMouseDown: (e) => isCopyingRef.current = true, onClick: seekToPopoverValue, "aria-label": "Go to timecode", children: "Go" })] }))] }));
}
